import IconButton from "@mui/material/IconButton";
import noChartIcon from "Explore/Assets/no_chart.png";
import {
  ImageCheck,
  LinkWrapper,
  LoadingSimpleComponent,
  VerticalBar,
  VideoCard,
  CollaborationLink,
} from "Explore/Components";
import { IconPack } from "Explore/Config/Icons";
import { ImageCheckType, SkeletonType } from "Explore/Config/ImageType";
import { Drawer, Tooltip } from "antd";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FacultyEducation,
  FacultyExperiences,
  FacultyHonor,
  OtherProfile,
} from "..";
import styles from "./BasicInformation.module.scss";
import { Button } from "@mui/material";
import { InfoCircleOutlined } from "@ant-design/icons";
import SectionTitle from "Explore/Components/SectionTitle";
import FacultyUnitInfo from "../FacultyUnitInfo";

const BasicInformationMobile = ({ summary, acclaimAchievements, faculty_id }) => {
  const [open, setOpen] = useState(false);
  const { unitInfo = {}, primaryDepartment = {} } = useMemo(() => {
    if (!Array.isArray(summary.data?.department)) return {};
    const primaryDepartment = summary.data?.department.find(
      (e) => e.is_primary
    );
    const normalDepartment = summary.data?.department.filter(
      (e) => !e.is_primary
    );
    const data = !!primaryDepartment
      ? !!primaryDepartment.parent_name
        ? {
            [primaryDepartment.parent_name]: {
              id: primaryDepartment.parent_id,
              explore_slug: primaryDepartment.parent_explore_slug,
              name: primaryDepartment.parent_name,
              subUnits: [primaryDepartment],
            },
          }
        : {
            [primaryDepartment.name]: {
              id: primaryDepartment.id,
              explore_slug: primaryDepartment.explore_slug,
              name: primaryDepartment.name,
              subUnits: [],
            },
          }
      : {};
    return {
      unitInfo: normalDepartment.reduce(
        (map, { parent_id, parent_name, parent_explore_slug, ...item }) => {
          let newValue = {};
          if (!!parent_name) {
            newValue = map[parent_name] ?? {
              name: parent_name,
              id: parent_id,
              explore_slug: parent_explore_slug,
              subUnits: [],
            };
            newValue.subUnits.push(item);
            map[parent_name] = newValue;
          } else {
            newValue = map[item.name] ?? {
              name: item.name,
              id: item.id,
              explore_slug: item.explore_slug,
              subUnits: [],
            };
            map[item.name] = newValue;
          }

          return map;
        },
        data
      ),
      primaryDepartment,
    };
  }, [summary.data?.department]);
  return (
    <>
      <div className={styles["basic-information-mobile"]}>
        <div className={styles["informations"]}>
          <div className={styles["informations__left"]}>
            <div className={styles["avatar"]}>
              <LoadingSimpleComponent
                className="shrink-0"
                component={
                  <div className={styles["avatar"]}>
                    <ImageCheck
                      url={summary.data?.photo}
                      alt={"avatar"}
                      type={ImageCheckType.FACULTY_AVATAR}
                    />
                  </div>
                }
                type={SkeletonType.IMAGE}
                isLoading={summary.isLoading}
              />
              <div className={styles["external-links"]}>
                <IconButton
                  aria-label="Personal web"
                  size="small"
                  target="_blank" rel="noreferrer"
                  href={summary.data?.homepage}
                >
                  {IconPack.homeWhite}
                </IconButton>
                {!!summary.data?.googlescholar && (
                  <IconButton
                    aria-label="Google scholar"
                    size="small"
                    target="_blank" rel="noreferrer"
                    href={summary.data.googlescholar}
                  >
                    {IconPack.google_scholar}
                  </IconButton>
                )}
              </div>
            </div>
          </div>
          <div className={styles["informations__right"]}>
            <LoadingSimpleComponent
              component={<h1 className={styles["name"]}>{summary.data?.name}</h1>}
              type={SkeletonType.INPUT}
              isLoading={summary.isLoading}
              className={styles["loading-text"]}
            />
            <LoadingSimpleComponent
              component={
                <LinkWrapper
                  to={`/institutions/${primaryDepartment.id}`}
                  component={
                    <div className={styles["primary-department"]}>
                      <p>{primaryDepartment.name}</p>
                    </div>
                  }
                />
              }
              type={SkeletonType.INPUT}
              isLoading={summary.isLoading}
              className={styles["loading-text"]}
            />
            <LoadingSimpleComponent
              component={
                <div className={styles["position"]}>
                  <p>{summary.data?.position}</p>
                </div>
              }
              type={SkeletonType.INPUT}
              isLoading={summary.isLoading}
              className={styles["loading-text"]}
            />
          </div>
        </div>
        <CollaborationLink
          entity={{
            id: summary.data?.id || faculty_id,
            name: summary.data?.name,
            type: "researcher",
            photo: summary.data?.photo,
            avatar: summary.data?.photo,
          }}
          variant="default"
          className={styles["mobile-collaboration-btn"]}
        />
        <LoadingSimpleComponent
          component={
            <Button
              className={styles["bio-info"]}
              onClick={() => setOpen(true)}
            >
              <InfoCircleOutlined style={{ alignSelf: "flex-start" }} />
              <span className={"dot-3"}>{summary.data?.biography}</span>
              <span style={{ marginLeft: "8px" }}>{IconPack.arrow}</span>
            </Button>
          }
          type={SkeletonType.INPUT}
          isLoading={summary.isLoading}
          className={styles["loading-text"]}
        />
      </div>
      <Drawer
        open={open}
        placement="right"
        title={`${summary.data?.name?.split(" ")?.[0]}'s Information`}
        closable
        onClose={() => setOpen(false)}
        footer={null}
      >
        <div className={styles["basic-info-drawer"]}>
          <div className={styles["drawer-item"]}>
            <SectionTitle title="Position" collapsible defaultOpen>
              <p>{summary.data?.position}</p>
            </SectionTitle>
          </div>

          <div className={styles["drawer-item"]}>
            <SectionTitle title="Departments" collapsible defaultOpen>
              <FacultyUnitInfo unitInfo={unitInfo} />
            </SectionTitle>
          </div>

          {summary.data?.video_presentations?.length > 0 && (
            <div className={styles["drawer-item"]}>
              <SectionTitle title="Video Presentations" collapsible defaultOpen>
                <div className={styles["video-cards"]}>
                  <div className={styles["video-cards__scroll"]}>
                    {summary.data?.video_presentations?.map((url, index) => (
                      <VideoCard key={index} videoUrl={url} />
                    ))}
                  </div>
                </div>
              </SectionTitle>
            </div>
          )}

          <div className={styles["drawer-item"]}>
            <SectionTitle title="Biography" collapsible>
              <p>{summary.data?.biography}</p>
            </SectionTitle>
          </div>

          {acclaimAchievements.honors?.length > 0 && (
            <div className={styles["drawer-item"]}>
              <SectionTitle title="Honors" collapsible>
                <FacultyHonor
                  data={{
                    data: acclaimAchievements.honors,
                    isLoading: acclaimAchievements.isLoading,
                    source_url: acclaimAchievements.honor_awards_source_url,
                  }}
                />
              </SectionTitle>
            </div>
          )}

          {acclaimAchievements.experiences?.length > 0 && (
            <div className={styles["drawer-item"]}>
              <SectionTitle title="Experiences" collapsible>
                <FacultyExperiences
                  data={{
                    data: acclaimAchievements.experiences,
                    isLoading: acclaimAchievements.isLoading,
                    source_url: acclaimAchievements.work_experience_source_url,
                  }}
                />
              </SectionTitle>
            </div>
          )}
          {acclaimAchievements.education?.length > 0 && (
            <div className={styles["drawer-item"]}>
              <SectionTitle title="Education" collapsible>
                <FacultyEducation
                  data={{
                    data: acclaimAchievements.education,
                    isLoading: acclaimAchievements.isLoading,
                    source_url: acclaimAchievements.education_source_url,
                  }}
                />
              </SectionTitle>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default BasicInformationMobile;
